import { useEffect, useMemo, useState } from 'react';
import type { Address } from 'viem';
import { parseEther } from 'viem';
import { useBalance } from 'wagmi';
import { shortenHash } from '@soundxyz/utils/const';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { Transaction } from '@context/transaction/transaction';
import { useEthToUsdValue } from '@hooks/useEthToUsd';
import { useTransactionFlow } from '@hooks/useTransactionFlow';
import { chainFromId } from '@utils/chain';
import { etherscanForTransaction, formatWeiToEthForDisplay } from '@utils/helpers';

const TX_STATUS_TOAST_DURATION = 1000 * 60 * 5;

export const useTransferFunds = ({
  amountInEth,
  recipientAddress,
  chainId,
}: {
  amountInEth: string;
  recipientAddress: Address | null;
  chainId: number;
}) => {
  const { authUserAddress } = useAuth();
  const { getSigner, isSwitchingNetwork } = useConnectedAuth();

  const { safeHandleTransaction } = Transaction.useContainer();
  const { openToast } = useToast();
  const { ethToUsd } = useEthToUsdValue();

  const [isTransferStarted, setIsTransferStarted] = useState(false);

  const amountInWei = useMemo(() => parseEther(amountInEth), [amountInEth]);

  const {
    data: ethBalance,
    isLoading: isBalanceLoading,
    isError: isBalanceError,
  } = useBalance({
    address: authUserAddress ?? '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'wei',
    chainId,
  });

  const ethBalanceInEth = useMemo(
    () =>
      ethBalance?.value
        ? formatWeiToEthForDisplay({
            weiValue: ethBalance?.value.toString(),
            maxDecimals: 4,
          })
        : '0',
    [ethBalance?.value],
  );

  const usdEthBalance = useMemo(
    () => (ethToUsd ? (ethToUsd * Number(ethBalanceInEth)).toFixed(2) : undefined),
    [ethBalanceInEth, ethToUsd],
  );

  const isAmountMoreThanZero = useMemo(() => {
    return amountInWei > BigInt(0);
  }, [amountInWei]);

  const isSufficientFunds = useMemo(() => {
    return amountInWei <= (ethBalance?.value ?? BigInt(0));
  }, [amountInWei, ethBalance?.value]);

  const isLoading = isSwitchingNetwork || isTransferStarted;

  const disabled =
    !isAmountMoreThanZero ||
    !isSufficientFunds ||
    isBalanceLoading ||
    isBalanceError ||
    isTransferStarted;

  useEffect(() => {
    if (isBalanceError) {
      // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
      openToast({
        text: 'Unable to check your balance. Please try again later.',
        variant: 'error',
      });
    }
  }, [isBalanceError, openToast]);

  const state = useMemo(() => {
    if (isSwitchingNetwork) {
      return 'Switch chain in wallet';
    }
    if (!isSufficientFunds) {
      return 'Insufficient funds';
    }
    if (isTransferStarted) {
      return 'Confirm in wallet';
    }
    return 'Send';
  }, [isSufficientFunds, isSwitchingNetwork, isTransferStarted]);

  const transferFunds = useTransactionFlow({
    chainId,
    onChainSwitchFail() {
      setIsTransferStarted(false);
    },
    onAuth: async () => {
      if (!recipientAddress) {
        return;
      }

      setIsTransferStarted(true);

      const signer = await getSigner(chainId);

      const { success, transactionHash } = await safeHandleTransaction({
        chainId,
        contractMethod: 'SEND_FUNDS',
        openProcessingModal: true,
        transactionFn: () =>
          signer.sendTransaction({
            to: recipientAddress,
            value: amountInWei,
            chain: chainFromId(chainId),
          }),
      });

      if (success) {
        const shortenedRecipient = shortenHash(recipientAddress, 3, 4);
        openToast({
          text: `Send complete! [${amountInEth}] ETH was sent to ${shortenedRecipient}.`,
          variant: 'success',
          ctaText: 'View on Etherscan',
          ctaHref: etherscanForTransaction({
            transactionHash,
            chainId,
          }),
          duration: TX_STATUS_TOAST_DURATION,
        });
      }

      setIsTransferStarted(false);
    },
  });

  return {
    transferFunds,
    ethBalanceInEth,
    usdEthBalance,
    isBalanceLoading,
    isBalanceError,
    isLoading,
    disabled,
    state,
  };
};
